"use client";

import type { RadiumShowcaseModel } from "@/lib/models-showcase";
import { cn } from "@/lib/utils";

interface Props {
  models: readonly RadiumShowcaseModel[];
}

const formatPriceDecimal = (n: number) => `$${n.toFixed(2)}`;

const formatTokenCount = (n: number) => n.toLocaleString();

/**
 * Pricing grid — parity with `radium-outcomes` `DataTable` + `ModelsPage` columns
 * (`DataTable.module.css`: surface-raised wrapper, uppercase headers, striped rows).
 */
export const ModelsPricingTable = ({ models }: Props) => (
  <section className="flex flex-col gap-[length:var(--ds-space-4)]" aria-labelledby="models-pricing-heading">
    <h2
      id="models-pricing-heading"
      className="m-0 text-[length:var(--ds-type-size-lg)] font-semibold tracking-[length:var(--ds-type-tracking-tight)] text-foreground"
    >
      Pricing Comparison
    </h2>

    <div
      className={cn(
        "overflow-x-auto rounded-[length:var(--ds-radius-md)] border border-[color:var(--ds-color-border-subtle)]",
        "bg-[color:var(--ds-color-surface-raised)] [-webkit-overflow-scrolling:touch]",
        "outline-none focus-visible:outline-none",
      )}
    >
      <table
        className={cn(
          "w-full border-collapse text-[length:var(--ds-type-size-sm)]",
          "[&_th]:border-x-0 [&_td]:border-x-0 [&_th]:border-t-0 [&_td]:border-t-0",
        )}
      >
        <thead>
          <tr>
            <th
              className={cn(
                "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-left align-middle",
                "text-[length:var(--ds-type-size-xs)] font-semibold tracking-[length:var(--ds-type-tracking-wide)] text-muted-foreground uppercase",
                "whitespace-nowrap border-b border-border",
              )}
            >
              Model
            </th>
            <th
              className={cn(
                "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                "text-[length:var(--ds-type-size-xs)] font-semibold tracking-[length:var(--ds-type-tracking-wide)] text-muted-foreground uppercase",
                "whitespace-nowrap border-b border-border",
              )}
            >
              Input / MTok
            </th>
            <th
              className={cn(
                "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                "text-[length:var(--ds-type-size-xs)] font-semibold tracking-[length:var(--ds-type-tracking-wide)] text-muted-foreground uppercase",
                "whitespace-nowrap border-b border-border",
              )}
            >
              Output / MTok
            </th>
            <th
              className={cn(
                "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                "text-[length:var(--ds-type-size-xs)] font-semibold tracking-[length:var(--ds-type-tracking-wide)] text-muted-foreground uppercase",
                "whitespace-nowrap border-b border-border",
              )}
            >
              Context Window
            </th>
            <th
              className={cn(
                "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                "text-[length:var(--ds-type-size-xs)] font-semibold tracking-[length:var(--ds-type-tracking-wide)] text-muted-foreground uppercase",
                "whitespace-nowrap border-b border-border",
              )}
            >
              Max Output
            </th>
          </tr>
        </thead>
        <tbody className="[&>tr:last-child>td]:border-b-0">
          {models.map((m, index) => (
            <tr
              key={m.id}
              className={cn("transition-colors", index % 2 === 1 && "bg-[color:var(--ds-color-surface-sunken)]")}
            >
              <td
                className={cn(
                  "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] align-top",
                  "border-b border-[color:var(--ds-color-border-subtle)] text-foreground",
                )}
              >
                <div className="flex flex-col gap-[length:var(--ds-space-0-5)]">
                  <span className="font-semibold text-foreground">{m.marketingName}</span>
                  <span className="text-[length:var(--ds-type-size-xs)] text-[color:var(--ds-color-text-tertiary)]">
                    {m.tagline}
                  </span>
                </div>
              </td>
              <td
                className={cn(
                  "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                  "border-b border-[color:var(--ds-color-border-subtle)] font-sans text-[length:var(--ds-type-size-sm)] font-medium tabular-nums text-foreground",
                )}
              >
                {formatPriceDecimal(m.inputPerMTok)}
              </td>
              <td
                className={cn(
                  "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                  "border-b border-[color:var(--ds-color-border-subtle)] font-sans text-[length:var(--ds-type-size-sm)] font-medium tabular-nums text-foreground",
                )}
              >
                {formatPriceDecimal(m.outputPerMTok)}
              </td>
              <td
                className={cn(
                  "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                  "border-b border-[color:var(--ds-color-border-subtle)] font-sans tabular-nums text-muted-foreground",
                )}
              >
                {formatTokenCount(m.contextWindow)}
              </td>
              <td
                className={cn(
                  "px-[length:var(--ds-space-4)] py-[length:var(--ds-space-3)] text-right align-middle",
                  "border-b border-[color:var(--ds-color-border-subtle)] font-sans tabular-nums text-muted-foreground",
                )}
              >
                {formatTokenCount(m.maxOutputTokens)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);
